import {useBatchContext} from '@/shared/contexts/batch'
import {Badge, Tabs} from '@chakra-ui/react'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {Timeline} from '@chakra-ui/react'
import {openLogDrawer} from '@/modules/system-logs/components/log-drawer/util'
import {LuCheck, LuInfo, LuPen, LuTrash} from 'react-icons/lu'
import {UserTag} from '@/shared/components/ui/tags/user-tag'
import {EventLog} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {getDatetimeFormatter} from '@cypro/ui/utils'
import {mapOldStateToEvents, mapOldValuesToLogPatches} from '@/modules/system-logs/functions'
import {useMemo} from 'react'

export function HistoryTab() {
  const {activities} = useBatchContext()
  const {t} = useTranslate('common')

  return (
    <Tabs.Content value="history">
      <DataSection>
        {!activities?.length && <EmptyState title={t('no_recorded_activity_yet')} />}

        {Boolean(activities?.length) && <BatchActivityTimeline activities={activities || []} />}
      </DataSection>
    </Tabs.Content>
  )
}

const actionIcons = {
  create: <LuCheck />,
  update: <LuPen />,
  delete: <LuTrash />,
  access: <LuInfo />,
} as const

const actionText = {
  create: 'new_lot',
  update: 'stock_adjusted',
  delete: 'lot_deleted',
  access: 'lot_accessed',
} as const

function BatchActivityTimeline({activities}: {activities: EventLog[]}) {
  const {t} = useTranslate('common')

  const mappedEvents = useMemo(() => {
    const chronological = [...activities].toReversed()
    return mapOldStateToEvents(chronological)
  }, [activities])

  console.log({activities})

  return (
    <Timeline.Root size="xl">
      {activities.map(activity => {
        const quantityLabel = getQuantityLabel(activity, mappedEvents)
        const details = activity.details as Record<string, string> | null | undefined
        const orderRef = details?.orderInternalId
        return (
          <Timeline.Item
            key={activity.id}
            onClick={() => openLogDrawer(activity)}
            cursor="pointer"
            px={4}
            transition="all 0.1s"
            borderLeft="3px solid transparent"
            _hover={{
              borderLeftColor: 'fg.subtle',
              transform: 'translateX(2px)',
            }}
          >
            <Timeline.Connector>
              <Timeline.Separator />
              <Timeline.Indicator bgColor="gray.muted" color="gray.fg">
                {activity.action && actionIcons[activity.action]}
              </Timeline.Indicator>
            </Timeline.Connector>
            <Timeline.Content>
              <Timeline.Title textStyle="sm">
                {activity.action && t(actionText[activity.action])}
                <UserTag user={activity.actor} />
                {quantityLabel && <Badge>{quantityLabel}</Badge>}
              </Timeline.Title>
              <Timeline.Description>
                {getDatetimeFormatter().format(activity.performedAt)}
                {orderRef && ` — ${t('order')}: ${orderRef}`}
              </Timeline.Description>
            </Timeline.Content>
          </Timeline.Item>
        )
      })}
    </Timeline.Root>
  )
}

function getQuantityLabel(activity: EventLog, mappedEvents: ReturnType<typeof mapOldStateToEvents>): string | null {
  if (activity.action === 'create') {
    const patch = activity.patches?.find(p => p.path === '/receivedQuantity' && p.op !== 'remove')
    return patch && 'value' in patch ? `${patch.value} units` : null
  }

  if (activity.action === 'delete') {
    const patch = activity.patches?.find(p => p.path === '/quantity' && p.op !== 'remove')
    return patch && 'value' in patch ? `${patch.value} units` : null
  }

  if (activity.action === 'update') {
    const changes = mapOldValuesToLogPatches(activity, mappedEvents)
    const quantityChange = changes.find(c => c.path === 'quantity')
    if (!quantityChange) return null

    if (quantityChange.oldValue == null) {
      const details = activity.details as Record<string, string> | null | undefined
      if (details?.usedQuantity) return `-${details.usedQuantity} units`
      return null
    }

    const diff = (quantityChange.value as number) - (quantityChange.oldValue as number)
    if (diff === 0) return null
    return diff > 0 ? `+${diff} units` : `${diff} units`
  }

  return null
}
